import type { ReactNode } from 'react';
import type { ProductInput } from '@discovr/types';
import { asProductArray } from './editRequestFormat';

const LINK_TARGET = '_blank';
const LINK_REL = 'noreferrer';
const EMPTY_VALUE = '—';

const NAME_HEADING = 'Product name';
const LINK_HEADING = 'Product link';
const NOTE_HEADING = 'Note for creators';

// Shared column template so the heading row and every product row stay aligned.
const COLUMN_GRID = 'grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-[1.2fr_1.2fr_1.6fr]';
const HEADING_CLASS = 'text-caption-medium uppercase tracking-wide text-muted-foreground';

export type ProductRowVariant = 'added' | 'removed';

/** A catalog row, optionally tagged with a diff variant (kept/removed/added). */
export interface ProductRow extends ProductInput {
  variant?: ProductRowVariant;
}

// Removed rows strike through; added rows get a dashed border. Both drop the
// background colour so the change reads via border/strike, not a coloured chip.
const VARIANT_ROW_CLASS: Record<ProductRowVariant, string> = {
  added: 'rounded-md border border-dashed border-border px-2 py-1.5 text-muted-foreground',
  removed: 'rounded-md px-2 py-1.5 text-muted-foreground line-through',
};

// Read-only product catalog as name/link/note columns. Rows tagged with
// `variant` render struck-through (removed) or highlighted (added).
export function ProductListReadonly({ products }: { products: ProductRow[] }) {
  return (
    <div className="flex flex-col gap-3">
      <div className={`hidden sm:grid ${COLUMN_GRID}`}>
        <span className={HEADING_CLASS}>{NAME_HEADING}</span>
        <span className={HEADING_CLASS}>{LINK_HEADING}</span>
        <span className={HEADING_CLASS}>{NOTE_HEADING}</span>
      </div>
      <div className="flex flex-col gap-3">
        {products.map((product, index) => (
          <ProductListRow
            key={`${product.product_id ?? product.name}-${product.variant ?? 'plain'}-${index}`}
            product={product}
          />
        ))}
      </div>
    </div>
  );
}

function ProductListRow({ product }: { product: ProductRow }) {
  const { variant } = product;
  const rowClassName = variant ? `${COLUMN_GRID} items-start ${VARIANT_ROW_CLASS[variant]}` : `${COLUMN_GRID} items-start`;
  // Variant rows inherit color from the row wrapper, so leave className unset.
  const nameClassName = variant ? undefined : 'text-body-sm-medium text-foreground';
  const noteClassName = variant ? undefined : 'text-caption-regular text-muted-foreground';
  return (
    <div className={rowClassName}>
      <ProductCell heading={NAME_HEADING} className={nameClassName}>
        {product.name || EMPTY_VALUE}
      </ProductCell>
      <ProductCell heading={LINK_HEADING}>
        <ProductLink url={product.product_url} plain={Boolean(variant)} />
      </ProductCell>
      <ProductCell heading={NOTE_HEADING} className={noteClassName}>
        {product.notes || EMPTY_VALUE}
      </ProductCell>
    </div>
  );
}

// Heading repeats per cell (sm:hidden) so values stay identifiable when stacked.
function ProductCell({ heading, className, children }: { heading: string; className?: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <span className={`mb-0.5 block sm:hidden ${HEADING_CLASS}`}>{heading}</span>
      <div className={className}>{children}</div>
    </div>
  );
}

function productFieldsEqual(a: ProductInput, b: ProductInput): boolean {
  return (
    a.name === b.name &&
    (a.product_url ?? '') === (b.product_url ?? '') &&
    (a.notes ?? '') === (b.notes ?? '')
  );
}

// Matches by product_id, falling back to name for id-less (unsaved) rows.
function matchesProduct(candidate: ProductInput, target: ProductInput): boolean {
  if (target.product_id) return candidate.product_id === target.product_id;
  return !candidate.product_id && candidate.name === target.name;
}

// Merges old/new into one table: unchanged rows plain; added rows and the new
// side of an in-place edit highlighted; removed rows and the old side of an
// edit struck-through. Consumes each match so duplicate names never collapse.
function buildProductDiffRows(before: ProductInput[], after: ProductInput[]): ProductRow[] {
  const remaining = [...before];
  const rows: ProductRow[] = [];
  for (const next of after) {
    const i = remaining.findIndex((prior) => matchesProduct(prior, next));
    if (i === -1) {
      rows.push({ ...next, variant: 'added' });
      continue;
    }
    const prior = remaining[i];
    if (prior === undefined) {
      rows.push({ ...next, variant: 'added' });
      continue;
    }
    remaining.splice(i, 1);
    if (productFieldsEqual(prior, next)) {
      rows.push({ ...next });
    } else {
      rows.push({ ...next, variant: 'added' });
      rows.push({ ...prior, variant: 'removed' });
    }
  }
  for (const prior of remaining) rows.push({ ...prior, variant: 'removed' });
  return rows;
}

// Products bypass the scalar old→new diff — they're name/link/note records,
// rendered as a merged before/after catalog table instead of a joined string.
export function ProductsDiffValue({ oldValue, newValue }: { oldValue: unknown; newValue: unknown }) {
  const before = asProductArray(oldValue);
  const after = asProductArray(newValue);
  const rows = buildProductDiffRows(before, after);
  return <ProductListReadonly products={rows} />;
}

// Product URLs are free text (see ProductRowsEditor) — only ever render one as
// a real link when it's an actual http(s) URL. A `javascript:`/other-scheme
// value throws at render on React 19.2 and is an XSS vector if it didn't;
// showing it as plain text instead degrades gracefully either way.
function safeHttpHref(raw: string): string | null {
  try {
    const { protocol } = new URL(raw);
    return protocol === 'http:' || protocol === 'https:' ? raw : null;
  } catch {
    return null;
  }
}

function ProductLink({ url, plain }: { url?: string; plain?: boolean }) {
  if (!url) return <span className="text-body-sm-regular text-muted-foreground">{EMPTY_VALUE}</span>;
  const href = safeHttpHref(url);
  const colorClass = plain ? '' : 'text-primary';
  if (!href) {
    return <span className={`text-body-sm-regular break-all ${plain ? '' : 'text-muted-foreground'}`}>{url}</span>;
  }
  return (
    <a
      href={href}
      target={LINK_TARGET}
      rel={LINK_REL}
      className={`text-body-sm-regular break-all underline-offset-2 hover:underline ${colorClass}`}
    >
      {url}
    </a>
  );
}
